import { useSettingEnableFDRASitewideBannerUpdates } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableFDRASitewideBannerUpdates";
import { IBrandDiscount } from "@faire/web-api--source/indigofair/data/IBrandDiscount";
import {
  AllowedFormats,
  intlDate,
} from "@faire/web/common/localization/date-fns/intlDate";
import { differenceInDays } from "date-fns/differenceInDays";
import { isAfter } from "date-fns/isAfter";
import { startOfDay } from "date-fns/startOfDay";
import { useCallback, useMemo } from "react";

import { getIsFaireDirectCreditPercentageBased } from "./getIsFaireDirectCreditPercentageBased";

const FORMATTED_EXPIRES_AT_DEFAULT = "MMMM d";

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

export const useFaireDirectFirstOrderIncentiveExpiresAtFields = (
  faireDirectFirstOrderIncentive: IBrandDiscount | undefined
) => {
  const {
    expires_at: expiresAt,
    amount_bps: amountBps,
    original_amount_bps: originalAmountBps,
  } = faireDirectFirstOrderIncentive ?? {};
  const isFaireDirectCreditPercentageBased =
    getIsFaireDirectCreditPercentageBased({ amountBps, originalAmountBps });
  const enableFDRASitewideBannerUpdates =
    useSettingEnableFDRASitewideBannerUpdates();

  const currentTime = useMemo(() => Date.now(), []);

  const definedExpiresAtOrZero = useMemo(() => {
    if (!expiresAt || isAfter(currentTime, expiresAt)) {
      return 0;
    }
    return expiresAt;
  }, [expiresAt, currentTime]);

  const getFormattedExpiresAt = useCallback(
    (format: AllowedFormats): string => {
      if (!expiresAt) {
        return "";
      }

      return intlDate(expiresAt, format ?? FORMATTED_EXPIRES_AT_DEFAULT);
    },
    [expiresAt]
  );

  const remainingDaysBeforeExpiry = useMemo(() => {
    if (!definedExpiresAtOrZero) {
      return 0;
    }

    if (!isFaireDirectCreditPercentageBased) {
      return getRemainingDaysBeforeExpiryInControlGroup(
        definedExpiresAtOrZero,
        currentTime
      );
    }

    return enableFDRASitewideBannerUpdates
      ? getRemainingDaysBeforeExpiryInTreatmentGroup(
          definedExpiresAtOrZero,
          currentTime
        )
      : getRemainingDaysBeforeExpiryInControlGroup(
          definedExpiresAtOrZero,
          currentTime
        );
  }, [
    definedExpiresAtOrZero,
    enableFDRASitewideBannerUpdates,
    isFaireDirectCreditPercentageBased,
    currentTime,
  ]);

  const isLastDayBeforeExpiry = definedExpiresAtOrZero
    ? getRemainingDaysBeforeExpiryInTreatmentGroup(
        definedExpiresAtOrZero,
        currentTime
      ) === 1
    : false;

  const isSecondLastDayBeforeExpiry = definedExpiresAtOrZero
    ? getRemainingDaysBeforeExpiryInTreatmentGroup(
        definedExpiresAtOrZero,
        currentTime
      ) === 2
    : false;

  return {
    expiresAt,
    getFormattedExpiresAt,
    remainingDaysBeforeExpiry,
    isLastDayBeforeExpiry,
    isSecondLastDayBeforeExpiry,
  };
};

const getRemainingDaysBeforeExpiryInTreatmentGroup = (
  expiresAt: number,
  currentTime: number
) => {
  // We can't use differenceInHours here because it rounds down the hours
  // For example, if there is less than one hour left, differenceInHours returns 0
  // which is not the desired behavior for calculating remaining days
  return Math.ceil((expiresAt - currentTime) / ONE_DAY_IN_MS);
};

const getRemainingDaysBeforeExpiryInControlGroup = (
  expiresAt: number,
  currentTime: number
) => {
  return differenceInDays(startOfDay(expiresAt), startOfDay(currentTime));
};
